import { X, ArrowLeft } from "@phosphor-icons/react";
import { useEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

// ---- Drawer stack (module-level singleton) -------------------------------
// Drawers register themselves on open; the level + isTop flags let nested
// "create-while-creating" flows (e.g. + ADD CUSTOMER from inside New Invoice)
// stack visibly without the inner drawer hiding the outer. Esc + backdrop
// click only act on the topmost drawer.
const drawerStack = {
  list: [],
  listeners: new Set(),
  push(id) {
    if (this.list.includes(id)) return;
    this.list = [...this.list, id];
    this.listeners.forEach(l => l());
  },
  pop(id) {
    if (!this.list.includes(id)) return;
    this.list = this.list.filter(x => x !== id);
    this.listeners.forEach(l => l());
  },
  subscribe(l) { this.listeners.add(l); return () => this.listeners.delete(l); },
};
const STACK_X_OFFSET_PX = 24; // how far each parent drawer slides left when a child opens

const subscribeStack = (cb) => drawerStack.subscribe(cb);
const getStackList = () => drawerStack.list;

function useDrawerStackSlot(open) {
  // Stable per-instance id (state initializer runs once, primitive read in render is pure).
  const [id] = useState(() => `d-${Math.random().toString(36).slice(2)}`);
  const list = useSyncExternalStore(subscribeStack, getStackList, getStackList);

  useEffect(() => {
    if (open) drawerStack.push(id);
    else drawerStack.pop(id);
    return () => drawerStack.pop(id);
  }, [open, id]);

  const idx = list.indexOf(id);
  const total = list.length;
  const level = idx < 0 ? 0 : idx;
  const isTop = total > 0 && idx === total - 1;
  // Parent drawers shift LEFT by 24px per child stacked on top so their right
  // edge peeks behind the child. Topmost stays at right:0.
  const stackOffsetPx = idx < 0 ? 0 : Math.max(0, (total - 1 - idx)) * STACK_X_OFFSET_PX;
  return { level, isTop, stackOffsetPx };
}

export const Modal = ({ open, onClose, title, children, footer, wide }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose} data-testid="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()} style={wide ? { maxWidth: "1100px", width: "92vw" } : undefined}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="heading text-lg">{title}</h3>
          <button onClick={onClose} className="btn btn-ghost btn-sm" data-testid="modal-close-btn"><X size={14} /></button>
        </div>
        <div className="p-4">{children}</div>
        {footer && <div className="p-4 border-t border-border flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
};

/**
 * Right-edge slide-out drawer. Sidebar stays visible/clickable thanks to a
 * higher z-index on the sidebar than the drawer backdrop. Backdrop dims the
 * page area only and click-outside closes. ESC also closes.
 *
 * The drawer panel is ALWAYS rendered (so the transition can animate the
 * out-state on close), but receives `pointer-events: none` when closed.
 */
export const Drawer = ({ open, onClose, title, children, footer, testid, size = "medium" }) => {
  const { level, isTop, stackOffsetPx } = useDrawerStackSlot(open);

  useEffect(() => {
    if (!open || !isTop) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isTop, onClose]);

  // z-index stacking — backdrop sits just below its panel; each level adds 2.
  const baseZ = 30; // matches CSS .drawer-backdrop default
  const backdropZ = baseZ + level * 2;
  const panelZ = baseZ + 10 + level * 2; // panel is +10 above its backdrop
  // CSS var consumed by .drawer-panel--open: shifts the panel LEFT when a child sits on top.
  const panelStyle = { zIndex: panelZ, "--drawer-stack-x": `-${stackOffsetPx}px` };
  const backdropStyle = { zIndex: backdropZ };

  // iter 51 — render via createPortal into document.body so that a parent
  // Drawer's `transform: translateX(...)` (used for the slide animation)
  // does NOT become the containing block for this child Drawer's
  // `position: fixed`. Without this, stacked sub-drawers were positioned
  // relative to the parent drawer's transformed box, pushing the panel
  // ~64px below the viewport — the SAVE/CANCEL footer was unreachable.
  const tree = (
    <>
      <div
        className={`drawer-backdrop ${open ? "drawer-backdrop--open" : ""}`}
        onClick={isTop ? onClose : undefined}
        style={backdropStyle}
        data-testid={testid ? `${testid}-backdrop` : "drawer-backdrop"}
        aria-hidden={!open}
      />
      <aside
        className={`drawer-panel drawer-panel--${size} ${open ? "drawer-panel--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        style={panelStyle}
        data-testid={testid || "drawer"}
        data-drawer-level={level}
        data-drawer-top={isTop ? "true" : "false"}
      >
        <div className="drawer-header">
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            data-testid={testid ? `${testid}-close-btn` : "drawer-close-btn"}
            title={level > 0 ? "Back (Esc)" : "Close (Esc)"}
          >
            {level > 0 ? <><ArrowLeft size={14} /> BACK</> : <><X size={14} /> CLOSE</>}
          </button>
          {title && <h3 className="heading text-base truncate ml-3 flex-1">{title}</h3>}
        </div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-footer">{footer}</div>}
      </aside>
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(tree, document.body);
};

export const Field = ({ label, children, testid }) => (
  <div className="mb-3">
    <label className="label-xs block mb-1.5">{label}</label>
    {testid ? <div data-testid={testid}>{children}</div> : children}
  </div>
);

export const StatusBadge = ({ status }) => {
  const map = {
    pending: "badge-yellow", assigned: "badge-blue", in_transit: "badge-blue",
    delivered: "badge-green", cancelled: "badge-red", paid: "badge-green",
    sent: "badge-blue", draft: "badge-gray", overdue: "badge-red",
    available: "badge-green", on_route: "badge-blue", off_duty: "badge-gray",
    todo: "badge-gray", in_progress: "badge-yellow", done: "badge-green",
    high: "badge-red", medium: "badge-yellow", low: "badge-gray",
  };
  return <span className={`badge ${map[status] || "badge-default"}`} data-testid={`status-${status}`}>{status?.replace("_", " ")}</span>;
};

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex items-end justify-between mb-6 pb-4 border-b border-border">
    <div>
      <div className="label-xs mb-1">{subtitle}</div>
      <h1 className="heading text-3xl">{title}</h1>
    </div>
    <div className="flex gap-2">{actions}</div>
  </div>
);


/**
 * Iter 26 — Upgrade-to-Core upsell banner. Shown at the top of pages where
 * a `contractor` org user hits a feature wall (create jobs, create customers,
 * sub-assign). Renders nothing for non-contractor orgs.
 *
 * Iter 27: the UPGRADE button is now a one-click flow — it POSTs to
 * /api/billing/upgrade-to-core (which creates a Stripe Checkout session
 * tagged metadata.upgrade_to_core=true) and redirects the user there.
 * On return, /upgrade-success flips org_type and fires confetti.
 *
 * Props:
 *  - feature: short string for the "Unlock X" headline (e.g. "create your own jobs")
 *  - testid: optional override; defaults to "upgrade-banner"
 *  - orgType: required — the current user.org_type value
 */
import { Link as _UpsellLink } from "react-router-dom";
import { ArrowSquareOut, Lightning, CircleNotch } from "@phosphor-icons/react";
import { useState as _useState } from "react";
import { api as _api, formatErr as _formatErr } from "../lib/api";

export const UpgradeBanner = ({ orgType, feature, testid = "upgrade-banner" }) => {
  const [loading, setLoading] = _useState(false);
  const [err, setErr] = _useState("");

  if (orgType !== "contractor") return null;

  const startUpgrade = async () => {
    setErr("");
    setLoading(true);
    try {
      const r = await _api.post("/billing/upgrade-to-core", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      setErr(_formatErr(e));
      setLoading(false);
    }
  };

  return (
    <div
      className="surface px-4 py-3 mb-4 flex items-center gap-3 border-l-4"
      style={{ borderLeftColor: "#2563EB", background: "rgba(37,99,235,0.04)" }}
      data-testid={testid}
    >
      <Lightning size={20} weight="fill" className="text-blue-600 shrink-0" />
      <div className="flex-1 text-sm">
        <span className="font-medium">Upgrade to Core to {feature}.</span>
        <span className="text-muted-foreground"> One click — your contractor data stays put.</span>
        {err && <div className="text-xs text-destructive mt-1" data-testid={`${testid}-error`}>{err}</div>}
      </div>
      <button
        type="button"
        onClick={startUpgrade}
        disabled={loading}
        className="btn btn-primary btn-sm"
        data-testid={`${testid}-cta`}
      >
        {loading ? <><CircleNotch size={12} className="animate-spin" /> STARTING…</> : <>UPGRADE <ArrowSquareOut size={12} /></>}
      </button>
    </div>
  );
};
